import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, MessageSquareText, ShieldCheck, ArrowLeft, MapPin, Printer, FileText, Save, Plus, Trash2, QrCode, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const TEXT_FIELDS = [
  ['title', 'Title'],
  ['intro', 'Intro line'],
  ['tagline', 'Tagline'],
  ['slogan_1', 'Slogan 1'],
  ['slogan_2', 'Slogan 2'],
  ['disclaimer', 'Disclaimer (required on the sheet)'],
  ['qr_destination', 'QR destination (where the Door Talk QR sends people)'],
  ['contact_line', 'Campaign contact line'],
  ['footer_text', 'Footer text'],
];

export default function AdminDoorTalk() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState('full');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/door-talk-sheet').then((r) => {
      setData(r.data);
      setError(false);
      setForm({ ...r.data.config, questions: (r.data.config.questions || []).map((q) => ({ ...q })) });
      if (r.data.last?.pdf) setResult(r.data.last);
    }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/door-talk-sheet', form);
      toast.success('Door Talk Sheet saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.put('/admin/door-talk-sheet', form);
      const r = await api.post('/admin/door-talk-sheet/generate', { format });
      setResult(r.data);
      toast.success(`${r.data.format_label} ready to print`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="door-talk-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the Door Talk Sheet.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data || !form) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQ = (i, k, v) => setForm((f) => ({ ...f, questions: f.questions.map((q, j) => (j === i ? { ...q, [k]: v } : q)) }));

  return (
    <div data-testid="admin-door-talk">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><MessageSquareText className="h-6 w-6 text-[#C9A84C]" /> Door Talk Sheet</h1>
          <p className="text-sm text-slate-500 mt-1">A pocket-size answer sheet for trusted volunteers — the most common questions residents ask at the door.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="door-talk-back-launch-btn">
            <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
          </Button>
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="door-talk-poster-drops-btn">
            <Link to="/admin/poster-drops?tab=volunteers"><MapPin className="h-4 w-4 mr-1.5" /> Volunteer Summary</Link>
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="door-talk-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          {TEXT_FIELDS.map(([key, label]) => (
            <div key={key}>
              <p className="text-sm font-bold text-[#1a2744] mb-1">{label}</p>
              {['contact_line', 'footer_text'].includes(key)
                ? <Textarea value={form[key] || ''} onChange={(e) => set(key, e.target.value)} rows={2} data-testid={`door-talk-field-${key.replace(/_/g, '-')}`} />
                : <Input value={form[key] || ''} onChange={(e) => set(key, e.target.value)} className="h-11" data-testid={`door-talk-field-${key.replace(/_/g, '-')}`} />}
            </div>
          ))}
          {data.tracked_qr_url && (
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="door-talk-tracked-url">
              <QrCode className="h-3.5 w-3.5 text-[#C9A84C]" /> Tracked QR link: <span className="font-mono break-all">{data.tracked_qr_url}</span> — scans count separately as "Door Talk Sheet" in QR Analytics.
            </p>
          )}

          <div className="space-y-3" data-testid="door-talk-questions">
            <p className="text-sm font-bold text-[#1a2744]">Resident questions & answers</p>
            {form.questions.map((q, i) => (
              <div key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`door-talk-question-${i}`}>
                <div className="flex gap-2 items-start">
                  <Input value={q.q} placeholder="Question" onChange={(e) => setQ(i, 'q', e.target.value)} className="h-10 text-sm flex-1" data-testid={`door-talk-q-${i}`} />
                  <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 shrink-0" onClick={() => set('questions', form.questions.filter((_, j) => j !== i))} data-testid={`door-talk-q-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <Textarea value={q.a} placeholder="Answer (plain language)" onChange={(e) => setQ(i, 'a', e.target.value)} rows={2} className="text-sm" data-testid={`door-talk-a-${i}`} />
              </div>
            ))}
            <Button variant="outline" size="sm" className="h-9 font-bold" onClick={() => set('questions', [...form.questions, { q: '', a: '' }])} data-testid="door-talk-add-question-btn">
              <Plus className="h-4 w-4 mr-1" /> Add question
            </Button>
          </div>

          <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="door-talk-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Door Talk Sheet
          </Button>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 p-4" data-testid="door-talk-generate-panel">
            <p className="font-serif font-bold text-[#1a2744] mb-2">Print it</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.entries(data.formats).map(([k, label]) => (
                <Button key={k} onClick={() => setFormat(k)} variant={format === k ? 'default' : 'outline'} size="sm" className={`h-10 font-bold btn-press ${format === k ? 'bg-[#1a2744] text-white' : ''}`} data-testid={`door-talk-format-${k}`}>{label}</Button>
              ))}
            </div>
            <Button onClick={generate} disabled={generating} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full sm:w-auto" data-testid="door-talk-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} Generate {data.formats[format]}
            </Button>
            <p className="text-[11px] text-slate-400 mt-2">Saves your edits first, then builds the PDF and PNG preview. Hand distribution only — nothing is published automatically.</p>
          </div>

          {result && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="door-talk-result">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-bold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> {result.format_label || 'Sheet'} generated</p>
                <Button onClick={() => window.open(fileUrl(result.pdf), '_blank', 'noopener')} size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid="door-talk-open-pdf-btn">
                  <FileText className="h-3.5 w-3.5 mr-1" /> Open PDF
                </Button>
              </div>
              <img src={`${fileUrl(result.png)}?t=${result.generated_at || ''}`} alt="Door Talk Sheet preview" className="mt-3 rounded border border-slate-200 w-full bg-white" data-testid="door-talk-preview-img" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
